import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@apollo/client";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Palette, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { GET_MY_BRAND_KITS, DELETE_MY_BRAND_KIT, UPDATE_MY_BRAND_KIT, GET_MY_DATA } from "@/queries/queries";
import { toast } from "sonner";
import { useTour } from "@reactour/tour";
import { withTour } from "@/components/withTour";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { BrandKitListingCard } from "@/components/brand-kits/BrandKitListingCard";
import { shouldShowBrandKitsCount } from "@/lib/brand-kit/shouldShowBrandKitsCount";
import { TEMPLATE_NEW_PATH } from "@/lib/brand-kit/templateRoutes";

const useTourSteps = () => {
  const { t } = useTranslation();
  return [
    {
      selector: '#brand-kits-page-title',
      content: t('brandKits.tour.welcome'),
      position: 'bottom',
    },
    {
      selector: '#create-brand-kit-button',
      content: t('brandKits.tour.createButton'),
      position: 'left',
    },
    {
      selector: '#brand-kits-grid',
      content: t('brandKits.tour.grid'),
      position: 'top',
    },
    {
      selector: '#help-button',
      content: t('brandKits.tour.helpButton'),
      position: 'bottom',
    },
  ];
};

interface BrandKit {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
  isPublic: boolean;
  data: unknown;
  previewVideoUrl?: string | null;
  previewPosterUrl?: string | null;
}

const BrandKitsContent = () => {
  const { t } = useTranslation();
  const { setIsOpen, setCurrentStep } = useTour();
  const [searchTerm] = useState("");
  const { data, loading, error, refetch } = useQuery(GET_MY_BRAND_KITS);
  const { data: userData, loading: userLoading } = useQuery(GET_MY_DATA);
  const [deleteBrandKit, { loading: deleting }] = useMutation(DELETE_MY_BRAND_KIT);
  const [updateBrandKit, { loading: updating }] = useMutation(UPDATE_MY_BRAND_KIT);
  const { dialog: quotaUpsellDialog, openQuotaUpsell } = useQuotaUpsellDialog();

  const startTour = () => {
    setCurrentStep(0);
    setIsOpen(true);
  };

  const transformedBrandKits: BrandKit[] = data?.myBrandKits?.map((kit: { id: string; name?: string | null; createdAt: string; updatedAt: string; is_default?: boolean | null; is_public?: boolean | null; data?: unknown; preview_video_url?: string | null; preview_poster_url?: string | null }) => ({
    id: kit.id,
    name: kit.name || t('brandKits.unnamed'),
    createdAt: kit.createdAt,
    updatedAt: kit.updatedAt,
    isDefault: kit.is_default || false,
    isPublic: kit.is_public || false,
    data: kit.data,
    previewVideoUrl: kit.preview_video_url,
    previewPosterUrl: kit.preview_poster_url,
  })) || [];

  const filteredBrandKits = transformedBrandKits
    .filter((kit) => !kit.isPublic)
    .filter((kit) => kit.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const userBrandKitsCount = transformedBrandKits.filter(kit => !kit.isPublic).length;
  const maxBrandKits = userData?.myData?.subscription?.plan?.max_brand_kits;
  const canCreateBrandKit = maxBrandKits === undefined || maxBrandKits === null || userBrandKitsCount < maxBrandKits;

  const handleDelete = async (id: string) => {
    if (deleting) return; // Prevent multiple clicks

    try {
      await deleteBrandKit({
        variables: { id },
        onCompleted: () => {
          toast.success(t('brandKits.deletedSuccess'));
          refetch(); // Refresh the list
        },
        onError: (error) => {
          console.error("Error deleting brand kit:", error);
          toast.error(t('brandKits.errorDeleting'));
        }
      });
    } catch (error) {
      console.error("Error deleting brand kit:", error);
      toast.error(t('brandKits.errorDeleting'));
    }
  };

  const handleSetDefault = async (id: string) => {
    if (updating) return; // Prevent multiple clicks

    try {
      await updateBrandKit({
        variables: {
          id,
          input: {
            is_default: true
          }
        },
        onCompleted: () => {
          toast.success(t('brandKits.setAsDefaultSuccess'));
          refetch(); // Refresh the list
        },
        onError: (error) => {
          console.error("Error setting brand kit as default:", error);
          toast.error(t('brandKits.errorSettingDefault'));
        }
      });
    } catch (error) {
      console.error("Error setting brand kit as default:", error);
      toast.error(t('brandKits.errorSettingDefault'));
    }
  };

  if (loading || userLoading) {
    return (
      <Layout onStartTour={startTour}>
        <div className="w-full p-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">{t('brandKits.loadingBrandKits')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout onStartTour={startTour}>
        <div className="w-full p-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Palette className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('brandKits.errorLoadingBrandKits')}</h3>
              <p className="text-muted-foreground text-center mb-4">
                {t('brandKits.errorLoadingBrandKitsDesc')}
              </p>
              <Button onClick={() => refetch()}>
                {t('brandKits.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout onStartTour={startTour}>
      <div className="w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 id="brand-kits-page-title" className="text-3xl font-bold">{t('brandKits.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('brandKits.subtitle')}
            </p>
            {shouldShowBrandKitsCount(maxBrandKits) && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('brandKits.brandKitsCount', {
                  count: userBrandKitsCount,
                  max: maxBrandKits,
                })}
              </p>
            )}
          </div>
          {canCreateBrandKit ? (
            <Link to={TEMPLATE_NEW_PATH}>
              <Button id="create-brand-kit-button" className="gap-2">
                <Plus className="w-4 h-4" />
                {t('brandKits.createNewBrandKit')}
              </Button>
            </Link>
          ) : (
            <Button
              id="create-brand-kit-button"
              className="gap-2"
              onClick={() => openQuotaUpsell('brand_kits')}
            >
              <Plus className="w-4 h-4" />
              {t('brandKits.createNewBrandKit')}
            </Button>
          )}
        </div>

        {/* Brand Kits Grid */}
        {filteredBrandKits.length === 0 ? (
          <Card id="brand-kits-grid">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Palette className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {searchTerm ? t('brandKits.noBrandKitFound') : t('brandKits.noBrandKitCreated')}
              </h3>
              <p className="text-muted-foreground text-center mb-4">
                {searchTerm
                  ? t('brandKits.adjustSearch')
                  : t('brandKits.startCreatingBrandKit')
                }
              </p>
              {!searchTerm && canCreateBrandKit && (
                <Link to={TEMPLATE_NEW_PATH}>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('brandKits.createFirstBrandKit')}
                  </Button>
                </Link>
              )}
              {!searchTerm && !canCreateBrandKit && (
                <Button onClick={() => openQuotaUpsell('brand_kits')}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('brandKits.createFirstBrandKit')}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div id="brand-kits-grid" className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filteredBrandKits.map((kit) => (
              <BrandKitListingCard
                key={kit.id}
                id={kit.id}
                name={kit.name}
                isDefault={kit.isDefault}
                data={kit.data}
                previewVideoUrl={kit.previewVideoUrl}
                previewPosterUrl={kit.previewPosterUrl}
                deleting={deleting}
                updating={updating}
                onSetDefault={handleSetDefault}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
        {quotaUpsellDialog}
      </div>
    </Layout>
  );
};

// Wrapper component to provide tour steps with translations
const BrandKitsWithTour = () => {
  const tourSteps = useTourSteps();
  const BrandKitsComponent = withTour(BrandKitsContent, 'autoclipper-brand-kits-tour-seen', tourSteps);
  return <BrandKitsComponent />;
};

export default BrandKitsWithTour;
